import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartShopping, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { NavigationBarButton } from "../../../screens/header/subcomponents/NavigationBarButton";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCartContext } from "../../../../contexts/CartContext";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { getCartItemsCurrency } from "../../../screens/order-create/models/CartItem";
import PropTypes from "prop-types";

export function CartNavMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role, currency, organization, workshopMode } = useProfileContext();

  const { cartSummary, isUpdating, activeOrder, items } = useCartContext();
  const hasCartItems = (cartSummary?.totalQuantity ?? 0) > 0;

  function handleToCart() {
    navigate("/order/place");
  }

  // Use active order total if available (e.g. on confirmation page with delivery/packaging fees),
  // otherwise fallback to cart summary total
  const displayTotal = activeOrder
    ? activeOrder.subTotal
    : cartSummary?.totalPrice;

  // The totals above are plain sums of the price rows' amounts (no FE
  // conversion), so the label must be the ROWS' currency — the profile
  // currency relabeled foreign amounts under act-as (EUR header on DKK
  // sums). The active order draft already carries the row-derived currency.
  const displayCurrency =
    activeOrder?.currency ?? getCartItemsCurrency(items, currency);

  if (role === Role.Admin) {
    return <AdminQueueNavMenu />;
  }

  if ((role === Role.EndUser && organization?.hidePrices) || workshopMode) {
    return null;
  }

  return (
    <div>
      <NavigationBarButton className={"px-5 w-[200px]"} onClick={handleToCart}>
        <div className="flex justify-between space-x-4">
          <CartTypography className="content-center">
            {t("compMisc.items")}{" "}
            {isUpdating ? (
              <FontAwesomeIcon
                size={"lg"}
                icon={faSpinner}
                className="animate-spin"
              />
            ) : (
              <b>{cartSummary?.totalQuantity}</b>
            )}
          </CartTypography>
          <CartTypography className="content-center">
            {t("compMisc.total")}{" "}
            {isUpdating ? (
              <FontAwesomeIcon
                size={"lg"}
                icon={faSpinner}
                className="animate-spin"
              />
            ) : (
              <b>{`${displayCurrency} ${displayTotal?.toFixed(2)}`}</b>
            )}
          </CartTypography>
          <div className={"content-center relative"}>
            {hasCartItems && (
              <div
                className={
                  "h-[7px] w-[7px] rounded-full bg-success absolute top-[-2px] -right-1"
                }
              />
            )}
            <FontAwesomeIcon size={"lg"} icon={faCartShopping} />
          </div>
        </div>
      </NavigationBarButton>
    </div>
  );
}

export const CartTypography = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>((props, ref) => {
  const { className, ...rest } = props;
  return (
    <p
      ref={ref}
      className={`text-xxs capitalize text-left ${className ?? ""}`}
      {...rest}
    >
      {props.children}
    </p>
  );
});

CartTypography.displayName = "CartTypography";
CartTypography.propTypes = {
  className: PropTypes.string,
};

function AdminQueueNavMenu() {
  const { t } = useTranslation();
  const { logoRepository, designRepository } = useRepositoriesContext();
  // React Query hooks: the header remounts several times during app startup, and the
  // previous imperative fetch fired on every mount (~5x per page load). The shared query
  // keys dedupe those into one request per queue, cached, still polling every 60s.
  const { data: logoCounts, isLoading: isLoadingLogos } =
    logoRepository.useImportLogosQueueCounts();
  const { data: designCounts, isLoading: isLoadingDesigns } =
    designRepository.useImportDesignsQueueCounts();
  const isLoading = isLoadingLogos || isLoadingDesigns;

  const map = new Map<string, number>();
  (logoCounts ?? []).forEach((c) => {
    const key = (c.queueName ?? "").toLowerCase();
    map.set(key, c.count ?? 0);
  });
  const importCount = map.get("import-logos-queue") ?? 0;
  const logoCheckCount = map.get("logo-check-endpoint") ?? 0;
  const designQueueCount = (designCounts ?? []).reduce(
    (total, queue) => total + (queue.count ?? 0),
    0
  );

  return (
    <div className="flex flex-col space-y-2 justify-center">
      <div className="px-4 flex justify-between space-x-2">
        <CartTypography className="content-center">
          {t("compMisc.logoCheckQueue")}
        </CartTypography>
        <CartTypography className="content-center">
          {isLoading ? (
            <FontAwesomeIcon
              size={"lg"}
              icon={faSpinner}
              className="animate-spin"
            />
          ) : (
            <b>{logoCheckCount}</b>
          )}
        </CartTypography>
      </div>
      <div className="px-4 flex justify-between space-x-2">
        <CartTypography className="content-center">
          {t("compMisc.logoQueue")}
        </CartTypography>
        <CartTypography className="content-center">
          {isLoading ? (
            <FontAwesomeIcon
              size={"lg"}
              icon={faSpinner}
              className="animate-spin"
            />
          ) : (
            <b>{importCount}</b>
          )}
        </CartTypography>
      </div>
      <div className="px-4 flex justify-between space-x-2">
        <CartTypography className="content-center">
          {t("compMisc.designQueue")}
        </CartTypography>
        <CartTypography className="content-center">
          {isLoading ? (
            <FontAwesomeIcon
              size={"lg"}
              icon={faSpinner}
              className="animate-spin"
            />
          ) : (
            <b>{designQueueCount}</b>
          )}
        </CartTypography>
      </div>
    </div>
  );
}
